import { Dictionary } from '@tableau/data-structures';

export function keyValueStoreGenerator() {
  return new KeyValueStore();
}

/**
 * Due to the fact that vqlweb/scriptsharp (host app) is not a React module,
 * we don't get any of the niceties that react provides for us, e.g.: don't render if props don't change.
 * This caused the rendering of @type {DataQualityWarningsInWebAuthoring} to flicker as the GraphQL query was made
 * because the component was re-rendered and mounted on every refresh in the host app.
 *
 * So we needed a way to store the content coming from the graph to both avoid incessant Graph API requests,
 * as well as the flickering that comes from those queries.
 * By storing the previous response somewhere we can pass that content along to the loading renderer for our component which hides the flicker.
 *
 * We had initially used SessionStorage, which disappears on tab closure and is supposed to be scoped to
 * protocol, domain, and port triple - but is not safe against XSS.
 * Since both SessionStorage and LocalStorage are not safe against XSS, so here we have a class that mimics the API of
 * SessionStorage and we can rely on the host app to hold onto a store for us.
 *
 * The host app doesn't need to know about the functions within this class though since it just
 * maintains a handle on the store and passes it into @type {DataQualityWarningsInWebAuthoringProps}
 */
export class KeyValueStore {
  state: Dictionary<string> = {};

  setItem(key: string, value: string) {
    this.state[key] = value;
  }

  getItem(key: string): string | null {
    return this.state[key] ?? null;
  }

  tryGetAndParse<T>(key: string): T | null {
    try {
      const json = this.getItem(key);
      return !json ? null : (JSON.parse(json) as T);
    } catch {
      return null;
    }
  }

  removeItem(key: string) {
    this.state[key] = undefined;
  }

  clear() {
    this.state = {};
  }
}
